import { observer, useLocalObservable } from 'mobx-react';
import { useRef, useEffect } from 'react';
import { useAgentSearch } from './AgentSearchContext';
import AgentSearchInput from './components/AgentSearchInput';
import AgentSearchResults from './components/AgentSearchResults';
import IconButton, { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

const AgentSearchModal = observer(() => {
  const searchState = useAgentSearch();
  const modalRef = useRef(null);

  const state = useLocalObservable(() => ({
    isRendered: false,
    setIsRendered: (value = false) => (state.isRendered = value),
    overlayHeight: window.visualViewport?.height
      ? window.visualViewport.height + 2000
      : window.innerHeight + 2000,
    updateOverlayHeight: () => {
      const height = window.visualViewport?.height || window.innerHeight;
      state.overlayHeight = height + 2000;
    }
  }));

  // Component lifecycle
  useEffect(() => {
    state.setIsRendered(true);

    const onViewportResize = () => {
      state.updateOverlayHeight();
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewportResize);
    } else {
      window.addEventListener('resize', onViewportResize);
    }

    return () => {
      state.setIsRendered(false);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onViewportResize);
      } else {
        window.removeEventListener('resize', onViewportResize);
      }
    };
  }, [state]);

  // Prevent body scroll when modal is open while preserving scroll position
  useBodyScrollLock(searchState.isModalOpen);

  // Click outside to close
  useEffect(() => {
    if (!searchState.isModalOpen) return;

    const clickEvent = (e) => {
      if (modalRef?.current && !modalRef.current.contains(e.target)) {
        searchState.closeModal();
      }
    };

    document.addEventListener('click', clickEvent, { capture: true });
    return () => {
      document.removeEventListener('click', clickEvent, { capture: true });
    };
  }, [searchState.isModalOpen]);

  // Escape key to close
  useEffect(() => {
    if (!searchState.isModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        searchState.closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [searchState.isModalOpen]);

  // Don't render if modal is closed
  if (!searchState.isModalOpen) {
    return null;
  }

  const getModalTitle = () => {
    switch (searchState.modalType) {
      case 'chat':
        return 'Search your chats';
      case 'project':
        return 'Search your projects';
      default:
        return 'Search';
    }
  };

  const getPlaceholder = () => {
    switch (searchState.modalType) {
      case 'chat':
        return 'Search your chats...';
      case 'project':
        return 'Search your projects...';
      default:
        return 'Search...';
    }
  };

  return (
    <div className="overlay agent-search-overlay" style={{ height: state.overlayHeight + 'px' }}>
      <div className="container agent-search-modal" ref={modalRef}>
        {/* Close button positioned in upper right corner */}
        <button
          className="agent-search-close-btn"
          onClick={searchState.closeModal}
          type="button"
          aria-label="Close search modal"
        >
          <img src="/icons/clean/modal_close_x.svg" alt="Close" />
        </button>

        {/* Search input acts as the header */}
        <AgentSearchInput placeholder={getPlaceholder()} />

        {/* Results area */}
        <AgentSearchResults />
      </div>
    </div>
  );
});

export default AgentSearchModal;
